import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { RealtimeChannel } from "@supabase/supabase-js";
import {
  projectStatusProjection,
  toProjectStatus,
  type ProjectStatus,
} from "@/domains/projects/status/projectStatus";
import {
  parsePermissionCatalog,
  type MemberProjectAccessRow,
  type PermissionCatalog,
  type PermissionCatalogRow,
  type ProjectPermissionsPayload,
} from "@/lib/memberProjectPermissions";
import { isMissingFunctionError } from "@/stores/permissions/repository";
import { ISSUE_ATTACHMENTS_FOLDER_NAME } from "@/domains/documents/constants/systemFolders";

type OrgRow = Database["public"]["Tables"]["organisations"]["Row"];
type OrgUserRoleRow = Database["public"]["Tables"]["org_user_roles"]["Row"];
type UserProfileRow = Database["public"]["Tables"]["user_profiles"]["Row"];
type UserBuildingAssignmentRow = Database["public"]["Tables"]["user_building_assignments"]["Row"];
type OrganizationInviteRow = Database["public"]["Tables"]["organization_invites"]["Row"];

export interface UserInvitation {
  id: number;
  invite_token: string;
  invitee_email: string;
  invited_role: string;
  status: string;
  expires_at: string;
  created_at: string;
  org_id: string;
  organisations?: {
    name: string;
    domain?: string;
  } | null;
}

/**
 * Create a new organization with the specified name and owner.
 * Generates a UUID for the organization, creates the organization record,
 * assigns the owner role to the user, and refreshes the auth session.
 *
 * @param name - The name of the organization
 * @param userId - The user profile ID of the organization owner
 * @returns Object with success flag and the created organization ID
 * @throws Error if organization creation or role assignment fails
 */
export async function createOrganization(
  name: string,
  userId: number
): Promise<{ success: true; orgId: string }> {
  const orgId = uuidv4();

  const { error: orgError } = await supabase.from("organisations").insert({
    id: orgId,
    name,
    owner_id: userId,
    created_by: userId,
  });

  if (orgError) {
    throw new Error(`Failed to create organization: ${orgError.message}`);
  }

  const { error: roleError } = await supabase.from("org_user_roles").insert({
    user_id: userId,
    org_id: orgId,
    role: "org_owner",
    created_by: userId,
  });

  if (roleError) {
    throw new Error(`Failed to assign owner role: ${roleError.message}`);
  }

  await refreshAuthSession();

  return { success: true, orgId };
}

/**
 * Update organization settings.
 */
export async function updateOrganization(
  orgId: string,
  updates: {
    name?: string;
    domain?: string | null;
    settings?: Record<string, unknown>;
    /** Storage path in the public `org-logos` bucket; null clears the logo. */
    logo_url?: string | null;
  }
) {
  const { error } = await supabase.from("organisations").update(updates).eq("id", orgId);

  if (error) throw new Error(error.message);
  return { success: true } as const;
}

/**
 * Fetch organization user roles for a specific organization.
 */
export async function fetchOrgUserRoles(orgId: string): Promise<OrgUserRoleRow[]> {
  const { data, error } = await supabase.from("org_user_roles").select("*").eq("org_id", orgId);

  if (error) throw new Error(error.message);
  return (data ?? []) as OrgUserRoleRow[];
}

/**
 * Fetch user profiles by user IDs.
 */
export async function fetchUserProfiles(
  userIds: number[]
): Promise<
  Pick<UserProfileRow, "id" | "first_name" | "last_name" | "email" | "avatar_url" | "color">[]
> {
  const { data, error } = await supabase
    .from("user_profiles")
    .select("id, first_name, last_name, email, avatar_url, color")
    .in("id", userIds);

  if (error) throw new Error(error.message);
  return (data ?? []) as Pick<
    UserProfileRow,
    "id" | "first_name" | "last_name" | "email" | "avatar_url" | "color"
  >[];
}

/**
 * Fetch user building assignments for a specific organization.
 * Returns a map of user_id to array of building_ids.
 */
export async function fetchUserBuildingAssignments(
  orgId: string
): Promise<Record<number, number[]>> {
  const { data, error } = await supabase
    .from("user_building_assignments")
    .select("user_id, building_id")
    .eq("org_id", orgId);

  if (error) throw new Error(error.message);

  // Group assignments by user_id
  const assignmentsByUser: Record<number, number[]> = {};
  (data ?? []).forEach((assignment) => {
    if (!assignmentsByUser[assignment.user_id]) {
      assignmentsByUser[assignment.user_id] = [];
    }
    assignmentsByUser[assignment.user_id].push(assignment.building_id);
  });

  return assignmentsByUser;
}

/**
 * Update user role and status for an organization.
 */
export async function updateUserRole(
  orgId: string,
  userId: number,
  currentRole: string,
  updates: { role?: string; status?: string },
  modifiedBy: number
) {
  const { error } = await supabase
    .from("org_user_roles")
    .update({ ...updates, modified_by: modifiedBy })
    .eq("org_id", orgId)
    .eq("user_id", userId)
    .eq("role", currentRole);

  if (error) throw new Error(error.message);
  return { success: true } as const;
}

/**
 * Change a member's org role via the tier-checked RPC (org_set_member_role).
 * The RPC enforces the owner/admin tier rules and is the ONLY path for org
 * users to change roles — direct org_user_roles writes are locked to platform
 * admins. Owners may set org_admin/subcontractor; admins may set subcontractor;
 * org_owners and the owner tier cannot be changed from the app.
 */
export async function setMemberRole(orgId: string, userId: number, newRole: string) {
  const { error } = await supabase.rpc("org_set_member_role", {
    p_org_id: orgId,
    p_target_user_id: userId,
    p_new_role: newRole,
  });
  if (error) throw new Error(error.message);
  return { success: true } as const;
}

/**
 * Remove a member from the org via the tier-checked RPC (org_remove_member).
 * Deletes the member's role plus their building/project assignments for the org.
 */
export async function removeMember(orgId: string, userId: number) {
  const { error } = await supabase.rpc("org_remove_member", {
    p_org_id: orgId,
    p_target_user_id: userId,
  });
  if (error) throw new Error(error.message);
  return { success: true } as const;
}

export interface OrgProjectOption {
  id: number;
  name: string;
  buildingId: number;
  buildingName: string;
  status: ProjectStatus;
}

/** Index building rows by id, so a project can be labelled with its name. */
function buildingNameById(rows: Array<{ id: number; name: string | null }>): Map<number, string> {
  return new Map(rows.map((building) => [building.id, building.name ?? "Building"]));
}

/**
 * Ids per `.in()` filter. PostgREST takes those filters in the query string, so
 * one request carrying a member's whole assignment set — uncapped, and a
 * long-lived subcontractor can hold thousands — grows a URL past the proxy limit
 * and comes back 414. These queries throw on error, and the Manage-projects
 * dialog turns a throw into a dead end, so an admin would lose the ability to
 * edit access at exactly the account where it matters most. 200 ids is ~1.5 kB
 * of filter, comfortably inside every limit in the path.
 */
const ID_FILTER_CHUNK_SIZE = 200;

/** Split ids into `.in()`-sized batches. An empty list yields no batches. */
function chunkIds(ids: number[], size = ID_FILTER_CHUNK_SIZE): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
}

/**
 * Run one `.in("id", …)` query per batch and concatenate the rows. Rejects on
 * the first batch that errors: a partial answer here reads as "those ids do not
 * exist", which every caller would act on.
 */
async function fetchInBatches<T>(
  ids: number[],
  runBatch: (
    batch: number[]
  ) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>
): Promise<T[]> {
  const results = await Promise.all(chunkIds(ids).map(runBatch));
  const failed = results.find((result) => result.error);
  if (failed?.error) throw new Error(failed.error.message);
  return results.flatMap((result) => result.data ?? []);
}

/**
 * Look up specific buildings in an org, by id. Scoped by org_id like every
 * other building/project query here: the ids come from org-scoped project rows,
 * but the tenant filter is stated in the query rather than left to FK
 * consistency plus RLS.
 */
async function fetchBuildingsByIds(
  orgId: string,
  buildingIds: number[]
): Promise<Array<{ id: number; name: string | null; archived: boolean | null }>> {
  return fetchInBatches<{ id: number; name: string | null; archived: boolean | null }>(
    buildingIds,
    (batch) =>
      supabase.from("buildings").select("id, name, archived").eq("org_id", orgId).in("id", batch)
  );
}

/**
 * Shape project rows into picker options, building-name-then-project-name
 * ordered. A building with no row in the map keeps its projects under the
 * "Building" fallback rather than losing them.
 */
function toProjectOptions(
  rows: Array<{
    id: number;
    name: string | null;
    building_id: number;
    project_status?: string | null;
  }>,
  buildingNames: Map<number, string>
): OrgProjectOption[] {
  return rows
    .map((p) => ({
      id: p.id,
      name: p.name ?? "Project",
      buildingId: p.building_id,
      buildingName: buildingNames.get(p.building_id) ?? "Building",
      status: toProjectStatus(p.project_status),
    }))
    .sort((a, b) => a.buildingName.localeCompare(b.buildingName) || a.name.localeCompare(b.name));
}

/** PostgREST caps a response at db.max-rows (1000 here), so the folder reads page. */
const PAGE_SIZE = 1000;

/**
 * Drain a select page by page. The two folder reads go through this: a folder
 * past the cap gets no checkbox at all, so it can never be denied — that is a
 * hole in the feature rather than a display glitch, and unlike the project offer
 * list the folder list is org-wide, so a thousand rows is ordinary.
 *
 * `fetchPage` must apply a total order — paging without one can repeat or skip
 * rows between requests.
 */
async function fetchAllPages<T>(
  fetchPage: (
    from: number,
    to: number
  ) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await fetchPage(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const page = data ?? [];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

/**
 * Fetch the org's offerable projects — not archived, not hidden, and not in an
 * archived building — with their building name, for the subcontractor project
 * pickers. Owners/admins see all org projects via RLS.
 *
 * This is an "what may be offered" list, never an authority on what a member
 * may keep: both queries can come back short (PostgREST's max-rows cap, a
 * narrower RLS policy), so no caller may derive a revocation from a project's
 * absence here. ManageSubcontractorProjectsDialog resolves assignments this list
 * doesn't cover through fetchProjectsByIds instead of pruning them.
 */
export async function fetchOrgProjects(orgId: string): Promise<OrgProjectOption[]> {
  const projectColumns = await projectStatusProjection("id, name, building_id");
  const [projectsRes, buildingsRes, archivedBuildingsRes] = await Promise.all([
    supabase
      .from("building_projects")
      .select(projectColumns)
      .eq("org_id", orgId)
      .eq("hidden", false)
      .eq("archived", false)
      // PostgREST caps this response (max-rows, 1000 on hosted Supabase) and an
      // unordered query has no defined truncation point, so which projects an
      // org past the cap gets offered would vary per request. Ordering makes the
      // offer list reproducible; it does not make it complete — an org with more
      // offerable projects than the cap needs a server-side (RPC/view) list, not
      // a wider client filter.
      .order("id"),
    supabase.from("buildings").select("id, name").eq("org_id", orgId),
    // Ordered for the same reason as the projects query above, and with more at
    // stake: an unordered response has no defined truncation point, so an org
    // past the cap would hold a given archived building in the exclusion set on
    // one request and not the next, re-offering its projects at random. Ordering
    // makes the set reproducible, not complete — see below.
    supabase.from("buildings").select("id").eq("org_id", orgId).eq("archived", true).order("id"),
  ]);

  if (projectsRes.error) throw new Error(projectsRes.error.message);
  if (buildingsRes.error) throw new Error(buildingsRes.error.message);
  if (archivedBuildingsRes.error) throw new Error(archivedBuildingsRes.error.message);

  // The exclusion set gets its own narrow query rather than being derived from
  // the name lookup above. Both truncate at PostgREST's max-rows cap, but they
  // fail in opposite directions: a name row lost to the cap only costs its
  // projects the "Building" fallback label, whereas an *archived* row lost to
  // the cap re-offers exactly the projects this filter exists to withhold. This
  // query returns one row per archived building instead of one per building, so
  // it takes an org with more archived buildings than the cap — not merely more
  // buildings than the cap — to truncate. Past that point no client-side
  // post-filter can be complete, whatever it is ordered by: the offer list then
  // needs the exclusion pushed into a server-side RPC/view, alongside the
  // projects query's own cap for the same reason.
  //
  // The exclusion is applied here rather than pushed into the projects query as
  // a `.not("building_id", "in", …)`: that filter travels in the query string,
  // and this set is capped at 1000 ids — five times the ~1.5 kB budget
  // ID_FILTER_CHUNK_SIZE keeps to. A 414 throws, and a throw makes this picker a
  // dead end, which is a worse outcome than the truncated list the push would be
  // buying back a few cap slots against.
  const archivedBuildingIds = new Set(
    (archivedBuildingsRes.data ?? []).map((building) => building.id)
  );

  return toProjectOptions(
    (projectsRes.data ?? []).filter((project) => !archivedBuildingIds.has(project.building_id)),
    buildingNameById(buildingsRes.data ?? [])
  );
}

export interface ResolvedProjectOption extends OrgProjectOption {
  /**
   * Whether this project really is one fetchOrgProjects withholds on purpose —
   * archived, hidden, or in an archived building — as read from the row itself,
   * not inferred from its absence from the offer list. An absent project may
   * equally be a perfectly live one the offer list simply didn't return (row
   * cap, a narrower RLS policy), and labelling that "archived or hidden" would
   * invite an admin to untick working access.
   */
  unavailable: boolean;
}

/**
 * Fetch specific projects by id with their building name, ignoring every
 * offerable filter — archived and hidden projects, and projects in an archived
 * building, all come back — and report per project whether one of those filters
 * is what withheld it.
 *
 * fetchOrgProjects deliberately excludes those, which on its own would make an
 * existing assignment to one unrevokable: the picker can't render a checkbox it
 * has no row for. Resolving the assigned ids here gives those assignments a
 * ticked row an owner/admin can still untick. Ids that come back from neither
 * query (row cap, a narrower RLS policy) are a different case — the caller
 * carries those through a save untouched.
 */
export async function fetchProjectsByIds(
  orgId: string,
  projectIds: number[]
): Promise<ResolvedProjectOption[]> {
  if (projectIds.length === 0) return [];

  const projectColumns = await projectStatusProjection("id, name, building_id, archived, hidden");
  const projects = await fetchInBatches<{
    id: number;
    name: string | null;
    building_id: number;
    archived: boolean | null;
    hidden: boolean | null;
    project_status?: string | null;
  }>(projectIds, (batch) =>
    supabase.from("building_projects").select(projectColumns).eq("org_id", orgId).in("id", batch)
  );
  if (projects.length === 0) return [];

  const buildingIds = Array.from(new Set(projects.map((project) => project.building_id)));
  const buildings = await fetchBuildingsByIds(orgId, buildingIds);
  const archivedBuildingIds = new Set(
    buildings.filter((building) => building.archived === true).map((building) => building.id)
  );
  // A building row the lookup didn't return is not evidence of archival, so it
  // leaves the project available — the same way fetchOrgProjects keeps such a
  // project listed rather than dropping it.
  const unavailableIds = new Set(
    projects
      .filter(
        (project) =>
          project.archived === true ||
          project.hidden === true ||
          archivedBuildingIds.has(project.building_id)
      )
      .map((project) => project.id)
  );

  return toProjectOptions(projects, buildingNameById(buildings)).map((project) => ({
    ...project,
    unavailable: unavailableIds.has(project.id),
  }));
}

export interface OrgFolderOption {
  id: number;
  name: string;
  /**
   * The folder's EFFECTIVE parent — `parent_id`, or the folder holding
   * `parent_document_id` where a folder hangs off a document instead. Both links
   * are real, and the server's folder_subtree_ids() walks both, so a tree built
   * on `parent_id` alone would draw a document's folder at the project root and
   * tick it while the server denies it with the branch it truly sits under.
   */
  parentId: number | null;
  /**
   * Set when the folder hangs off a document the lookup did not return — hard
   * deleted, or outside this admin's RLS scope. `parentId` then falls back to
   * null, which is NOT the same claim as "this folder sits at the project root":
   * the server resolves the link regardless and can deny the folder with a
   * branch this tree never drew it under. The picker labels such a folder rather
   * than presenting the root position as fact.
   */
  parentUnresolved?: boolean;
  projectId: number;
  folderPath: string | null;
  /** Soft-deleted or hidden folders are not rendered, but still cascade. */
  isDeleted: boolean;
  hidden: boolean;
}

type FolderRow = {
  id: number;
  name: string | null;
  parent_id: number | null;
  parent_document_id: number | null;
  project_id: number | null;
  folder_path: string | null;
  is_deleted: boolean | null;
  hidden: boolean | null;
};

/**
 * The folder's effective parent, and whether that position is known.
 *
 * `parent_id` wins wherever it is set, so a folder carrying both links never
 * consults the document lookup — exactly as COALESCE(f.parent_id, d.folder_id)
 * resolves it server-side.
 *
 * The document lookup answers three different things and they are kept apart: a
 * folder id (the document's own folder), `null` (the document IS at the project
 * root, so the folder is too — the server's COALESCE stops there as well), and
 * no entry at all (the document did not come back). Only the last is a gap in
 * what we know, and it is flagged rather than flattened into the root.
 */
function resolveFolderParent(
  folder: FolderRow,
  folderIdByDocument: Map<number, number | null>
): { parentId: number | null; parentUnresolved: boolean } {
  if (folder.parent_id !== null) return { parentId: folder.parent_id, parentUnresolved: false };
  if (folder.parent_document_id === null) return { parentId: null, parentUnresolved: false };
  const documentParentId = folderIdByDocument.get(folder.parent_document_id);
  if (documentParentId === undefined) return { parentId: null, parentUnresolved: true };
  return { parentId: documentParentId, parentUnresolved: false };
}

/** A project-scoped row as the picker wants it; `[]` for the building-scoped ones. */
function toFolderOption(
  folder: FolderRow,
  folderIdByDocument: Map<number, number | null>
): OrgFolderOption[] {
  if (folder.project_id === null) return [];
  const { parentId, parentUnresolved } = resolveFolderParent(folder, folderIdByDocument);
  const option: OrgFolderOption = {
    id: folder.id,
    name: folder.name ?? "Folder",
    parentId,
    projectId: folder.project_id,
    folderPath: folder.folder_path,
    isDeleted: folder.is_deleted === true,
    hidden: folder.hidden === true,
  };
  if (parentUnresolved) option.parentUnresolved = true;
  return [option];
}

/** Ids per request when looking documents up by id — they travel in the URL. */
const DOCUMENT_LOOKUP_CHUNK = 200;

/**
 * Where the given documents live, so a folder hanging off one can be drawn under
 * its real ancestor. Mirrors `COALESCE(f.parent_id, d.folder_id)` in
 * folder_subtree_ids(): a document at the project root (`folder_id` null) leaves
 * the folder at the root too, which is where the server's walk also stops.
 *
 * A row that comes back is recorded even when its `folder_id` is null, so the
 * absence of a key means one thing only: the document did not come back at all
 * (hard deleted, or outside this reader's RLS scope). That is not evidence the
 * folder sits at the root, and folding the two together would draw the folder
 * ticked at the project root while the server still denies it with the branch it
 * truly hangs under — the exact mis-draw this resolution exists to close. The
 * caller flags such a folder instead (OrgFolderOption.parentUnresolved).
 */
async function fetchFolderIdByDocument(documentIds: number[]): Promise<Map<number, number | null>> {
  const byDocument = new Map<number, number | null>();
  for (let i = 0; i < documentIds.length; i += DOCUMENT_LOOKUP_CHUNK) {
    const { data, error } = await supabase
      .from("documents")
      .select("id, folder_id")
      .in("id", documentIds.slice(i, i + DOCUMENT_LOOKUP_CHUNK));

    if (error) throw new Error(error.message);
    for (const row of data ?? []) byDocument.set(row.id, row.folder_id);
  }
  return byDocument;
}

/**
 * Fetch every project folder in the org for the subcontractor folder picker.
 * Deliberately a superset of what any single subcontractor's own folder tree
 * shows — excluding a folder they never see is a harmless no-op, whereas missing
 * one from the picker would be a hole in the grant.
 * project_id is nullable on the table (building-scoped legacy folders), so the
 * NULLs are filtered out in SQL and again when mapping to keep the id typed.
 *
 * Soft-deleted and hidden folders are FETCHED, only not rendered. They must be
 * in the set so that unticking a parent cascades onto them: a restore un-deletes
 * every ancestor, so a missing exclusion row would hand the whole subtree back.
 * A folder this list never returns is simply absent from the picker — an admin
 * cannot deny it, and an exclusion already stored against it lands in neither
 * delta array and survives a save untouched.
 *
 * Paged for the same reason: PostgREST caps a response at db.max-rows (1000), so
 * an org past that many folders would silently hand back a truncated list, and
 * the missing folders would have no checkbox to deny them with. Ordered by id,
 * not folder_path — paging needs a total order and folder_path is nullable; the
 * picker sorts by name for display anyway.
 *
 * A folder can hang off a DOCUMENT rather than a parent folder, and that link is
 * a parent to the server exactly as `parent_id` is, so the documents concerned
 * are resolved to their own folder here — one extra round trip, and only when
 * such folders exist at all. A document that does not come back leaves its
 * folder flagged `parentUnresolved`, not silently rooted; the picker says so
 * rather than drawing a position it does not know. See OrgFolderOption.parentId.
 */
export async function fetchOrgProjectFolders(orgId: string): Promise<OrgFolderOption[]> {
  const rows = await fetchAllPages<FolderRow>((from, to) =>
    supabase
      .from("document_folders")
      .select(
        "id, name, parent_id, parent_document_id, project_id, folder_path, is_deleted, hidden"
      )
      .eq("org_id", orgId)
      .not("project_id", "is", null)
      .order("id")
      .range(from, to)
  );

  const documentIds = [
    ...new Set(
      rows
        .filter((row) => row.parent_id === null && typeof row.parent_document_id === "number")
        .map((row) => row.parent_document_id as number)
    ),
  ];
  const folderIdByDocument =
    documentIds.length > 0
      ? await fetchFolderIdByDocument(documentIds)
      : new Map<number, number | null>();

  return withoutIssueAttachments(rows.flatMap((row) => toFolderOption(row, folderIdByDocument)));
}

/**
 * Drops each project's Issue Attachments folder, and anything under it, from what
 * the member access editor offers. Who sees it follows VIEW_PM in that project
 * (franklin-data-pipeline 20261006083147), and the database refuses to store a
 * folder exclusion on it, so a tick there could only mislead.
 *
 * Known by its reserved name at the project root, as the database knows it.
 */
export function withoutIssueAttachments(options: OrgFolderOption[]): OrgFolderOption[] {
  const byId = new Map(options.map((option) => [option.id, option]));
  const isRoot = (option: OrgFolderOption) =>
    option.name === ISSUE_ATTACHMENTS_FOLDER_NAME &&
    option.parentId === null &&
    !option.parentUnresolved;

  const underIssueAttachments = (option: OrgFolderOption): boolean => {
    const seen = new Set<number>();
    let current: OrgFolderOption | undefined = option;
    while (current && !seen.has(current.id)) {
      if (isRoot(current)) return true;
      seen.add(current.id);
      current = current.parentId === null ? undefined : byId.get(current.parentId);
    }
    return false;
  };

  return options.filter((option) => !underIssueAttachments(option));
}

/** One row of the /projects landing: a visible project + its building + counts. */
export interface OrgProjectOverview {
  projectId: number;
  projectName: string;
  projectDescription: string | null;
  projectCreatedAt: string;
  isDefault: boolean;
  buildingId: number;
  buildingName: string;
  docCount: number;
  openIssueCount: number;
  /** Every live issue, resolved and closed included. */
  /** Absent until the project_status migration's RPC is live. */
  issueCount: number | null;
  /** Assigned users plus the org's owners and admins, who reach every project. */
  memberCount: number;
  status: ProjectStatus;
}

interface OrgProjectOverviewRow {
  project_id: number;
  project_name: string;
  project_description: string | null;
  project_created_at: string;
  is_default: boolean;
  building_id: number;
  building_name: string;
  doc_count: number;
  open_issue_count: number;
  issue_count?: number;
  member_count: number;
  project_status?: string;
}

/**
 * Fetch every project the caller may see across the org, with its building and
 * per-project doc / issue / member counts and status, in one round trip — data for
 * the Project-mode landing page (grouped by building). Backed by the
 * get_org_projects_overview RPC, whose visibility mirrors the building_projects
 * select RLS (owners/admins: all; subcontractors: assigned only). Rows arrive
 * ordered building-name ASC, then newest project first.
 */
export async function fetchOrgProjectsOverview(orgId: string): Promise<OrgProjectOverview[]> {
  const { data, error } = await supabase.rpc("get_org_projects_overview", { p_org_id: orgId });
  if (error) throw new Error(error.message);
  // Every column except project_description is NOT NULL at the RPC level
  // (names are NOT NULL, counts are COUNT()::int, is_default is COALESCEd).
  return ((data ?? []) as OrgProjectOverviewRow[]).map((r) => ({
    projectId: r.project_id,
    projectName: r.project_name,
    projectDescription: r.project_description,
    projectCreatedAt: r.project_created_at,
    isDefault: r.is_default,
    buildingId: r.building_id,
    buildingName: r.building_name,
    docCount: r.doc_count,
    openIssueCount: r.open_issue_count,
    issueCount: r.issue_count ?? null,
    memberCount: r.member_count,
    status: toProjectStatus(r.project_status),
  }));
}

export interface ProjectsByBuilding {
  buildingId: number;
  buildingName: string;
  projects: OrgProjectOverview[];
}

/**
 * Group the (already building-ordered) overview rows into one section per
 * building, preserving the RPC's ordering (building name ASC, projects
 * newest-first within each group).
 */
export function groupProjectsByBuilding(rows: OrgProjectOverview[]): ProjectsByBuilding[] {
  const groups: ProjectsByBuilding[] = [];
  const byId = new Map<number, ProjectsByBuilding>();
  for (const row of rows) {
    let group = byId.get(row.buildingId);
    if (!group) {
      group = { buildingId: row.buildingId, buildingName: row.buildingName, projects: [] };
      byId.set(row.buildingId, group);
      groups.push(group);
    }
    group.projects.push(row);
  }
  return groups;
}

/**
 * Fetch the project ids a subcontractor is currently assigned to in an org.
 * Owners/admins may read any member's assignments via the upa_select RLS policy
 * (MANAGE_ORG_PERMISSIONS / MANAGE_PROJECT_PERMISSIONS); the member can read
 * their own.
 *
 * Paged, because Save asserts an end state: every id read here is re-sent, and
 * every assignment NOT sent is revoked. A short read (PostgREST's 1000-row cap)
 * would therefore silently strip a member of everything past the cap.
 */
export async function fetchSubcontractorProjectIds(
  orgId: string,
  userId: number
): Promise<number[]> {
  const rows = await fetchAllPages<{ project_id: number }>((from, to) =>
    supabase
      .from("user_project_assignments")
      .select("project_id")
      .eq("org_id", orgId)
      .eq("user_id", userId)
      .order("project_id")
      .range(from, to)
  );

  return rows.map((row) => row.project_id);
}

/**
 * Fetch the folder ids excluded from a subcontractor's project grants in an org.
 * Folder scope is a deny-list: no rows means full access to every folder of the
 * projects they are assigned to, which is what every existing grant looks like.
 *
 * Paged: excluding one subtree writes a row per descendant, so a single member
 * routinely holds more than PostgREST's 1000-row cap. A short read seeds the
 * dialog with an already-denied folder ticked — a wrong picture of a permission,
 * which an admin would read as access the member has.
 */
export async function fetchSubcontractorExcludedFolderIds(
  orgId: string,
  userId: number
): Promise<number[]> {
  const rows = await fetchAllPages<{ folder_id: number }>((from, to) =>
    supabase
      .from("user_project_folder_exclusions")
      .select("folder_id")
      .eq("org_id", orgId)
      .eq("user_id", userId)
      .order("folder_id")
      .range(from, to)
  );

  return rows.map((row) => row.folder_id);
}

/** Candidate ids per request: they travel in the query string, not the body. */
const FOLDER_SUBTREE_PROBE_CHUNK = 200;

/**
 * Which of `candidateFolderIds` sit inside the subtrees rooted at
 * `rootFolderIds`, answered by the server's own walk.
 *
 * The picker's index cannot answer this: it holds the folders as they were when
 * the dialog opened, and the question is asked about folders that appeared
 * since. `folder_subtree_ids()` follows both parent links and is SECURITY
 * DEFINER over roots in an organisation the caller belongs to, so an admin may
 * ask it about the member's denied branches.
 *
 * Asked with a filter rather than read whole: the expansion of a denied branch
 * is routinely larger than PostgREST's 1000-row cap, and a truncated answer here
 * would read as "not a descendant" — the false verdict this exists to avoid.
 * Chunked because the filter is a query-string `in`.
 */
export async function fetchFoldersInSubtrees(
  rootFolderIds: number[],
  candidateFolderIds: number[]
): Promise<Set<number>> {
  const found = new Set<number>();
  if (rootFolderIds.length === 0 || candidateFolderIds.length === 0) return found;

  for (let i = 0; i < candidateFolderIds.length; i += FOLDER_SUBTREE_PROBE_CHUNK) {
    const { data, error } = await supabase
      .rpc("folder_subtree_ids", { p_folder_ids: rootFolderIds })
      .in("folder_id", candidateFolderIds.slice(i, i + FOLDER_SUBTREE_PROBE_CHUNK));

    if (error) throw new Error(error.message);
    for (const row of data ?? []) found.add(row.folder_id);
  }

  return found;
}

/**
 * A subcontractor's project access as it should stand after the save: every
 * project they hold in this org, and every folder denied inside those projects.
 */
export interface ProjectScope {
  /** Assignments to keep. Anything not named here is revoked. */
  projectIds: number[];
  /** The deny-list, subtree-closed. Anything not named here is re-allowed. */
  excludedFolderIds: number[];
}

/**
 * Set a subcontractor's project- and folder-access to an exact end state.
 *
 * `org_set_subcontractor_project_scope` reconciles: it deletes every assignment
 * of this member in this org that `p_project_ids` does not name, and every
 * exclusion row on a *rendered* folder that `p_excluded_folder_ids` does not
 * name. The whole of a save is therefore the payload, and a caller that computes
 * it from an incomplete picture revokes access nobody asked it to. That drives
 * three requirements on the callers, all met in ManageSubcontractorProjectsDialog:
 *
 *   - Assignments the picker could not show still travel in `projectIds` —
 *     `keptOutOfPickerIds`, see buildPickerAssignments. Absence is evidence of
 *     nothing, and here absence is a revocation.
 *   - `fetchSubcontractorProjectIds` and `fetchSubcontractorExcludedFolderIds`
 *     page to completion, so the seed the end state is built from is whole.
 *   - The deny-list is sent subtree-CLOSED: every denied folder is named, not
 *     just the branch roots. The RPC treats each folder it is not given as kept
 *     and stops its subtree walk (folder_subtree_ids) there, so a roots-only
 *     payload denied the parent alone and left its subfolders visible.
 *     toggleFolderExclusion keeps the picker's set closed in both directions, so
 *     the set the admin sees is the set that is sent.
 *
 * An end state does mean a save re-asserts a picture seeded when the dialog
 * opened, so a change another admin made in the meantime is overwritten rather
 * than merged. That is the contract the server implements and it is the honest
 * one to speak: a delta cannot express "deny this branch except that folder"
 * against a function whose only vocabulary is a subtree, and disagreeing with
 * the server about what a save MEANS is how a deny-list ends up wider or
 * narrower than the admin was shown. The window is kept short instead, and what
 * happens inside it is checked rather than assumed — the dialog refetches on
 * every open, never refetches under an open picker, re-reads this member's
 * stored scope immediately before writing and refuses the save if it moved
 * (otherwise an assignment or denial another admin wrote would be reverted by a
 * picture that predates it), and re-checks each new grant against the project's
 * own row.
 *
 * One RPC, one transaction, because with folders in the picture the two halves
 * of a save are no longer independent. An assignment insert that lands while the
 * exclusion insert fails leaves the member holding the WHOLE project with none
 * of the denials the admin just asked for — over-privileged, and reported as a
 * failure. Fail-open on a permission narrowing is not a state worth being able
 * to reach, so there is nothing here to apply "partially".
 *
 * The RPC is SECURITY INVOKER, so authorization is unchanged: upa_modify /
 * upfe_modify (MANAGE_ORG_PERMISSIONS or per-project MANAGE_PROJECT_PERMISSIONS)
 * remain the only enforcement layer, and a subcontractor holds neither. It also
 * resolves every project id through building_projects on the org, so the array
 * cannot reach outside the organisation it is called for.
 *
 * Idempotent: saving the same scope twice is a no-op. Takes effect immediately —
 * per-project access is evaluated live by has_permission() against these tables,
 * not from the JWT, so the subcontractor does not need to re-authenticate for
 * changes within an org they already belong to.
 */
export async function setSubcontractorProjects(
  orgId: string,
  userId: number,
  scope: ProjectScope,
  assignedBy: number,
  projectPermissions?: ProjectPermissionsPayload
): Promise<{ success: true }> {
  const { error } = await supabase.rpc("org_set_subcontractor_project_scope", {
    p_org_id: orgId,
    p_user_id: userId,
    p_project_ids: scope.projectIds,
    p_excluded_folder_ids: scope.excludedFolderIds,
    p_assigned_by: assignedBy,
    // Omitted (not null) when the editor has no permissions to send, so a save
    // against a database that predates the argument still resolves.
    ...(projectPermissions ? { p_project_permissions: projectPermissions } : {}),
  });

  if (error) throw new Error(error.message);
  return { success: true } as const;
}

/**
 * The configurable per-project codes and whether the Member role grants each —
 * what the access editor renders, and what its "Contributor" preset means.
 *
 * `null` when the database predates per-project permissions
 * (franklin-data-pipeline migration 20260930101528): the editor then offers
 * project and folder access only, and says so.
 */
export async function fetchProjectPermissionCatalog(): Promise<PermissionCatalog | null> {
  const { data, error } = await supabase.rpc("get_project_permission_catalog", {
    p_role: "subcontractor",
  });
  if (error) {
    if (isMissingFunctionError(error)) return null;
    throw new Error(error.message);
  }
  return parsePermissionCatalog((data ?? []) as PermissionCatalogRow[]);
}

/**
 * One row per (assigned project, configurable code) for a member: the role
 * default, the stored override if any, and the effective answer — which is
 * `has_permission()` itself. Read by owners/admins through upp_select.
 *
 * Paged like the other seeds of an end-state save: the rows are assignments
 * times codes, so a member on ~110 projects passes PostgREST's cap, and a
 * project whose rows were cut off would be seeded from the role default — and
 * then saved over its real overrides. The RPC orders by (project, code).
 */
export async function fetchMemberProjectAccess(
  orgId: string,
  userId: number
): Promise<MemberProjectAccessRow[]> {
  return await fetchAllPages<MemberProjectAccessRow>((from, to) =>
    supabase
      .rpc("org_get_member_project_access", { p_org_id: orgId, p_user_id: userId })
      .range(from, to)
  );
}

/**
 * Subscribe to real-time changes for organization user roles.
 * Returns a RealtimeChannel that the caller must manage and clean up.
 *
 * @param orgId - Organization ID to subscribe to
 * @param onChangeCallback - Callback function invoked when changes occur
 * @returns RealtimeChannel instance for lifecycle management
 */
export function subscribeToOrgUserRoles(
  orgId: string,
  onChangeCallback: () => void
): RealtimeChannel {
  const channel = supabase
    .channel(`org_user_roles_changes_${orgId}`) // Unique channel per org
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "org_user_roles",
        filter: `org_id=eq.${orgId}`,
      },
      onChangeCallback
    )
    .subscribe();

  return channel;
}

/**
 * Unsubscribe and remove a RealtimeChannel.
 *
 * @param channel - The RealtimeChannel to unsubscribe from
 */
export function unsubscribeChannel(channel: RealtimeChannel): void {
  supabase.removeChannel(channel);
}

/**
 * Fetch user invitations by email.
 * Returns pending, non-expired invitations for the given email address.
 */
export async function fetchUserInvitations(email: string): Promise<UserInvitation[]> {
  const { data, error } = await supabase
    .from("organization_invites")
    .select(
      `
      id,
      invite_token,
      invitee_email,
      invited_role,
      status,
      expires_at,
      created_at,
      org_id,
      organisations(
        name,
        domain
      )
    `
    )
    .eq("invitee_email", email.toLowerCase().trim())
    .eq("status", "pending")
    .gt("expires_at", new Date().toISOString())
    .order("created_at", { ascending: false });

  if (error) throw new Error(`Failed to fetch invitations: ${error.message}`);
  return (data ?? []) as UserInvitation[];
}

/**
 * Accept an organization invitation.
 * Updates the invite status and creates organization membership.
 */
export async function acceptInvitation(inviteToken: string, userAuthId: string) {
  const { data: session, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session.session) {
    throw new Error("Not authenticated");
  }

  // Call the accept-organization-invite edge function
  const { data, error } = await supabase.functions.invoke("accept-organization-invite", {
    body: {
      inviteToken,
      userAuthId,
    },
  });

  if (error) {
    throw new Error(`Failed to accept invitation: ${error.message}`);
  }

  if (data?.error) {
    // Handle error responses from the edge function
    throw new Error(data.error);
  }

  if (!data?.success) {
    throw new Error("Failed to accept invitation");
  }

  return { success: true, data };
}

/**
 * Decline an organization invitation.
 * Calls the decline_organization_invitation RPC, which verifies the caller is
 * the invitee and the invite is still pending, then marks it declined.
 */
export async function declineInvitation(inviteToken: string) {
  const { data: session, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session.session) {
    throw new Error("Not authenticated");
  }

  const { data, error } = await supabase.rpc("decline_organization_invitation", {
    p_invite_token: inviteToken,
  });

  if (error) {
    throw new Error(`Failed to decline invitation: ${error.message}`);
  }

  // The RPC returns false when nothing was declined (invite not pending or not the caller's)
  if (!data) {
    throw new Error("Failed to decline invitation: invitation is no longer pending");
  }

  return { success: true };
}

/**
 * Refresh the current user's authentication session.
 * Used after accepting an invitation to update JWT claims.
 */
export async function refreshAuthSession() {
  const { error } = await supabase.auth.refreshSession();
  if (error) {
    throw new Error(`Failed to refresh session: ${error.message}`);
  }
}

/**
 * Resend an organization invitation.
 * Resets the expiration date to 7 days from now.
 *
 * @param inviteId - The ID of the invitation to resend
 * @returns The updated invitation data
 */
export async function resendInvite(inviteId: number) {
  const { data: session, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session.session) {
    throw new Error("Not authenticated");
  }

  const { data, error } = await supabase.functions.invoke("resend-organization-invite", {
    body: { invitation_id: inviteId },
  });

  if (error) {
    throw new Error(`Failed to resend invitation: ${error.message}`);
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  if (!data?.success) {
    throw new Error("Failed to resend invitation");
  }

  return { success: true, invitation: data.invitation };
}

/**
 * Cancel a pending organization invitation.
 *
 * Soft-cancel: sets the invite's status to 'cancelled' (a first-class value in the
 * organization_invites status CHECK constraint). Relies on the invites_update RLS
 * policy, which allows a caller holding MANAGE_ORG_INVITES on the org. There is no
 * DELETE policy on the table, so this is the correct way to retire an invite from
 * the client. Cancelled invites are filtered out of the org invites list.
 *
 * @param inviteId - The ID of the invitation to cancel
 */
export async function cancelInvite(inviteId: number): Promise<void> {
  const { data, error } = await supabase
    .from("organization_invites")
    .update({ status: "cancelled" })
    .eq("id", inviteId)
    .select("id");

  if (error) {
    throw new Error(`Failed to cancel invitation: ${error.message}`);
  }

  // RLS filters out rows the caller may not update, returning zero rows without an
  // error — treat that as a failure rather than reporting a false success.
  if (!data || data.length === 0) {
    throw new Error("Failed to cancel invitation");
  }
}

/**
 * Cancel several organization invitations at once — backs "Clear all expired".
 *
 * Same soft-cancel semantics as cancelInvite, in a single round trip. RLS drops
 * rows the caller may not update, so a short result means only some invites were
 * cancelled — those are cancelled for real, so the ids that made it through are
 * returned and the caller reports the partial outcome. Only a wholesale failure
 * (nothing cancelled) throws.
 *
 * @param inviteIds - IDs of the invitations to cancel
 * @returns The IDs actually cancelled
 */
export async function cancelInvites(inviteIds: number[]): Promise<number[]> {
  if (inviteIds.length === 0) return [];

  const { data, error } = await supabase
    .from("organization_invites")
    .update({ status: "cancelled" })
    .in("id", inviteIds)
    .select("id");

  if (error) {
    throw new Error(`Failed to cancel invitations: ${error.message}`);
  }

  if (!data || data.length === 0) {
    throw new Error("Failed to cancel invitations");
  }

  return data.map((row) => row.id);
}
